import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Loader2, AlertCircle, Building2, ArrowLeft, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import axios from 'axios';
import { API_URL } from '../lib/api';

export default function AuthPage() {
    const [searchParams] = useSearchParams();
    const initialMode = searchParams.get('mode') || 'login';
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [name, setName] = useState('');
    const [agencyName, setAgencyName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [publicReg, setPublicReg] = useState(null);
    const { login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API_URL}/api/app-settings/public`).then(r => setPublicReg(r.data.public_registration_enabled)).catch(() => setPublicReg(false));
    }, []);

    // If beta mode and trying to create agency, redirect to login
    useEffect(() => {
        if (publicReg === false && mode === 'create-agency') {
            setMode('login');
            setError('Les inscriptions publiques sont actuellement désactivées. Marge Auto est en bêta privée.');
        }
    }, [publicReg, mode]);

    const handleLogin = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        const result = await login(email, password);
        if (!result.success) setError(result.error);
        setLoading(false);
    };

    const handleCreateAgency = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        if (password !== passwordConfirm) { setError('Les mots de passe ne correspondent pas'); setLoading(false); return; }
        if (password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères'); setLoading(false); return; }
        if (!agencyName.trim()) { setError('Le nom de l\'agence est requis'); setLoading(false); return; }
        try {
            const { data } = await axios.post(`${API_URL}/api/auth/create-agency`, {
                agency_name: agencyName, responsable_name: name, email, password
            }, { withCredentials: true });
            window.location.href = '/dashboard';
        } catch (err) {
            setError(err.response?.data?.detail || 'Erreur lors de la création');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex bg-[#F5F7FA]">
            <SEOHead title="Connexion — Marge Auto" description="Connectez-vous à Marge Auto." path="/auth" noindex />
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md space-y-8 animate-fade-in">
                    {/* Logo */}
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-5 bg-[#6366F1]">
                            <span className="text-xl font-bold text-white font-['Inter']">M</span>
                        </div>
                        <h1 className="text-2xl font-semibold tracking-tight font-['Inter'] text-gray-900">Marge Auto</h1>
                        <p className="mt-2 text-gray-500 text-sm">Calculateur de marge pour véhicules d'occasion</p>
                    </div>

                    {/* Login form */}
                    {mode === 'login' && (
                        <form onSubmit={handleLogin} className="space-y-5">
                            {error && <ErrorBanner error={error} />}
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="email" placeholder="vous@exemple.fr" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" required data-testid="auth-email-input" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Mot de passe</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" required data-testid="auth-password-input" />
                                </div>
                            </div>
                            <Button type="submit" className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-medium py-2.5" disabled={loading} data-testid="auth-submit-btn">
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Se connecter
                            </Button>
                            <div className="text-center"><a href="/reset-password" className="text-sm text-[#6366F1] hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</a></div>
                        </form>
                    )}

                    {/* Create agency form */}
                    {mode === 'create-agency' && (
                        <form onSubmit={handleCreateAgency} className="space-y-4">
                            <div className="flex items-center gap-2 mb-2">
                                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400">
                                    <ArrowLeft className="w-4 h-4" />
                                </button>
                                <h2 className="text-lg font-semibold text-gray-900">Créer mon agence</h2>
                            </div>
                            {error && <ErrorBanner error={error} />}
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Nom de l'agence</Label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="text" placeholder="Simplicicar Toulon" value={agencyName} onChange={e => setAgencyName(e.target.value)} className="pl-10" required data-testid="agency-name-input" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Nom du responsable</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="text" placeholder="Jean Dupont" value={name} onChange={e => setName(e.target.value)} className="pl-10" required data-testid="agency-resp-input" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-600 text-xs">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input type="email" placeholder="vous@agence.fr" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" required data-testid="auth-email-input" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="text-gray-600 text-xs">Mot de passe</Label>
                                    <Input type="password" placeholder="••••••" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="auth-password-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-600 text-xs">Confirmation</Label>
                                    <Input type="password" placeholder="••••••" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} required minLength={6} data-testid="password-confirm-input" />
                                </div>
                            </div>
                            <Button type="submit" className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-medium py-2.5" disabled={loading} data-testid="auth-submit-btn">
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Créer mon agence
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>
                    )}

                    {/* Toggle */}
                    <div className="text-center space-y-3">
                        {mode === 'login' ? (
                            <button type="button" onClick={() => { setMode('create-agency'); setError(''); }} className="text-gray-500 hover:text-gray-700 text-sm" data-testid="auth-toggle-btn">
                                Pas encore d'agence ? <span className="text-[#6366F1] font-medium">Créer mon agence</span>
                            </button>
                        ) : (
                            <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-gray-500 hover:text-gray-700 text-sm" data-testid="auth-toggle-btn">
                                Déjà un compte ? <span className="text-[#6366F1] font-medium">Se connecter</span>
                            </button>
                        )}
                        <p className="text-[11px] text-gray-400 leading-relaxed">
                            Essayez Marge Auto gratuitement pendant 30 jours.<br />
                            Toutes les fonctionnalités sont incluses. Sans engagement.
                        </p>
                    </div>
                </div>
            </div>
            <div className="hidden lg:flex lg:w-1/2 bg-[#1F2937] items-end p-12">
                <div className="rounded-xl p-5 bg-white/5 border border-white/10">
                    <p className="text-base font-medium text-white">"Calculez instantanément vos marges réelles sur chaque véhicule"</p>
                    <p className="mt-2 text-gray-400 text-sm">TVA sur marge, frais, TVA déductible — tout en un clic</p>
                </div>
            </div>
        </div>
    );
}

function ErrorBanner({ error }) {
    return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
        </div>
    );
}
